/**
 * AppState-aware Supabase Realtime reconnect.
 *
 * Resolves the well-known regression in [supabase/realtime-js#463](https://github.com/supabase/realtime-js/issues/463):
 * an Expo app that has been backgrounded long enough for the WebSocket
 * to drop (≈30 s on iOS, longer on Android) does not reconnect on
 * resume. Channels remain "subscribed" client-side but receive no
 * events. The user perceives this as silent breakage — chat looks live
 * but doesn't update.
 *
 * Mirror of the auth auto-refresh AppState wiring in `lib/supabase.ts`.
 * Wire from the root layout (`app/_layout.tsx`) ONCE — calling this hook
 * from multiple screens would create duplicate listeners.
 *
 * The reconnect only fires when the backgrounded interval was long
 * enough to actually need it (`RECONNECT_THRESHOLD_MS`); brief inactive
 * states (status bar pull, control center) are left alone so we don't
 * thrash the WebSocket.
 */
import { useEffect, useRef } from 'react'
import { AppState, type AppStateStatus } from 'react-native'
import { supabase } from '../supabase'

const RECONNECT_THRESHOLD_MS = 25_000

export function useRealtimeReconnect() {
  const backgroundedAtRef = useRef<number | null>(null)

  useEffect(() => {
    const handleStateChange = (next: AppStateStatus) => {
      if (next === 'background' || next === 'inactive') {
        // Stamp the time we left active. We only reconnect on resume if
        // we were away long enough to have plausibly lost the socket.
        if (backgroundedAtRef.current === null) {
          backgroundedAtRef.current = Date.now()
        }
        return
      }

      if (next === 'active') {
        const backgroundedAt = backgroundedAtRef.current
        backgroundedAtRef.current = null

        if (backgroundedAt === null) return
        const awayFor = Date.now() - backgroundedAt
        if (awayFor < RECONNECT_THRESHOLD_MS) return

        if (__DEV__) {
          console.log(`[Tago] Realtime: foregrounded after ${awayFor} ms — reconnecting WebSocket`)
        }

        try {
          supabase.realtime.disconnect()
          supabase.realtime.connect()
        } catch (err) {
          if (__DEV__) console.warn('[Tago] Realtime reconnect failed:', err)
          // Non-fatal: next user action will retrigger any subscribed
          // hook's `useEffect` cleanup→subscribe cycle if this didn't.
        }
      }
    }

    const sub = AppState.addEventListener('change', handleStateChange)
    return () => sub.remove()
  }, [])
}
